// ─── Custom HTML on a journey step: see it, then inspect it ───────────────────
// A step set to Custom HTML used to be a six-row textarea of raw markup and
// nothing else. You could not tell at a glance whether a step had the right
// email in it, whether it had any HTML at all, or what it would look like —
// which is exactly how a journey went out looking like it had no custom HTML.
//
// So the RENDERED email leads and the code is a click away. The preview is the
// real thing: it goes through the same render the send uses (tracking links,
// unsubscribe footer, merge fields filled from a sample recipient), so what you
// see is what lands — not the raw paste. If that call fails it falls back to
// rendering the raw markup rather than showing nothing.
//
// Everything renders inside a sandboxed iframe with NO allow-scripts, so pasted
// or uploaded markup cannot run anything against the Pulse page it sits in.
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../lib/api.js';
import { useIsMobile } from '../lib/useIsMobile.js';

const EMAIL_W = 600;   // what an email is actually laid out for
const PHONE_W = 390;   // …and what it is actually read on
const CARD_H = 230;    // thumbnail height inside the 270px-wide step card

const tab = (on) => ({
  flex: 1, border: '1px solid var(--hairline)', borderRadius: 7, padding: '3px 6px', fontSize: 11,
  fontWeight: 700, cursor: 'pointer', background: on ? 'var(--brand)' : 'var(--bg)', color: on ? '#fff' : 'var(--muted)',
});
const ghost = { border: '1px dashed var(--hairline)', borderRadius: 7, padding: '4px 10px', fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer', background: 'none' };
const bytes = (n) => (n >= 1024 ? `${(n / 1024).toFixed(1)} KB` : `${n} bytes`);

// The rendered email in a sandboxed frame. `scale` shrinks a 600px email to fit
// a narrow card without reflowing it — an email squeezed to 260px would look
// broken in a way the real thing is not. `phone` is the opposite: it narrows the
// frame to a real handset viewport so the email DOES reflow, mobile media queries
// and all, which is the only way to see what a client will actually get.
function Frame({ html, scale = 1, height, phone = false }) {
  const w = phone ? PHONE_W : scale === 1 ? '100%' : EMAIL_W;
  return (
    <div style={{ height, overflow: scale === 1 ? 'auto' : 'hidden', background: phone ? 'var(--bg)' : '#fff', borderRadius: 8, border: '1px solid var(--hairline)', display: phone ? 'flex' : undefined, justifyContent: phone ? 'center' : undefined }}>
      <iframe
        title="Email preview" srcDoc={html} sandbox="" referrerPolicy="no-referrer" loading="lazy"
        style={{ width: w, maxWidth: '100%', height: scale === 1 ? height : height / scale, border: 0, display: 'block', transform: scale === 1 ? undefined : `scale(${scale})`, transformOrigin: 'top left', background: '#fff' }}
      />
    </div>
  );
}

export default function HtmlStepPreview({ node, entityId, onEdit, onUpload }) {
  const html = node.customHtml || '';
  const has = !!html.trim();
  // Preview leads when there IS something to look at; an empty step opens on the
  // code, because previewing nothing helps nobody.
  const [view, setView] = useState(has ? 'preview' : 'code');
  const [full, setFull] = useState(false);
  const [rendered, setRendered] = useState('');
  const timer = useRef(null);
  const mobile = useIsMobile();

  // True-to-send render, debounced. Falls back to the raw markup.
  useEffect(() => {
    clearTimeout(timer.current);
    if (!has) { setRendered(''); return undefined; }
    if (!entityId) { setRendered(html); return undefined; }
    let alive = true;
    timer.current = setTimeout(() => {
      api.actionPreviewEmail(entityId, { channel: 'email', contentMode: 'html', customHtml: html, subject: node.subject || '' })
        .then((r) => { if (alive) setRendered(r.html || html); })
        .catch(() => { if (alive) setRendered(html); });
    }, 400);
    return () => { alive = false; clearTimeout(timer.current); };
  }, [html, entityId, node.subject, has]);

  const code = (rows) => (
    <textarea
      style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--hairline)', borderRadius: 7, background: 'var(--bg)', color: 'var(--text)', padding: '5px 8px', resize: 'vertical', lineHeight: 1.4, fontFamily: 'ui-monospace, monospace', fontSize: 11 }}
      rows={rows} value={html} spellCheck={false}
      placeholder="<html>… full email HTML (links auto-tracked; unsubscribe added if missing)"
      onChange={(e) => onEdit(node.id, { customHtml: e.target.value })}
    />
  );

  return (
    <>
      <div style={{ display: 'flex', gap: 4 }}>
        <button type="button" style={tab(view === 'preview')} onClick={() => setView('preview')}>Preview</button>
        <button type="button" style={tab(view === 'code')} onClick={() => setView('code')}>{'<> Code'}</button>
      </div>

      {view === 'code' ? code(8) : has ? (
        <div
          role="button" tabIndex={0} title="Open full size"
          onClick={() => setFull(true)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setFull(true); } }}
          style={{ cursor: 'zoom-in', position: 'relative' }}
        >
          {rendered ? <Frame html={rendered} scale={252 / EMAIL_W} height={CARD_H} />
            : <div style={{ height: CARD_H, display: 'grid', placeItems: 'center', border: '1px solid var(--hairline)', borderRadius: 8, fontSize: 11.5, color: 'var(--muted)' }}>Rendering…</div>}
          {/* The frame swallows pointer events, so the whole thumbnail is covered. */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: 8 }} />
        </div>
      ) : (
        <div style={{ height: 74, display: 'grid', placeItems: 'center', border: '1px dashed var(--hairline)', borderRadius: 8, fontSize: 11.5, color: 'var(--muted)', textAlign: 'center', padding: 8 }}>
          No HTML in this step yet — it would send an empty email.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <label style={ghost}>⬆ Upload .html<input type="file" accept=".html,.htm,text/html" onChange={onUpload} style={{ display: 'none' }} /></label>
        {has && <button type="button" style={ghost} onClick={() => setFull(true)}>⛶ Full size</button>}
        {has && <span style={{ fontSize: 10.5, color: 'var(--muted)' }}>{bytes(html.length)}</span>}
      </div>

      {full && createPortal(
        <FullView
          node={node} html={html} rendered={rendered || html} mobile={mobile}
          onEdit={onEdit} onClose={() => setFull(false)} code={code}
        />, document.body,
      )}
    </>
  );
}

// Full-screen: the email at its real width, with the markup beside it (stacked
// on a phone) so you can find the bit you want to change and change it there.
function FullView({ node, html, rendered, mobile, onClose, code }) {
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onClose]);
  const [pane, setPane] = useState('preview'); // phones show one at a time
  // Phone first: it is how the email will actually be read, and it is where a
  // broken layout shows up (a laptop browser papers over markup a phone won't).
  const [asPhone, setAsPhone] = useState(true);
  const showPreview = !mobile || pane === 'preview';
  const showCode = !mobile || pane === 'code';
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: mobile ? 0 : 20 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: 'var(--card)', borderRadius: mobile ? 0 : 14, width: '100%', maxWidth: 1180, height: mobile ? '100%' : '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', borderBottom: '1px solid var(--hairline)' }}>
          <b style={{ fontSize: 13, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.subject || '(no subject)'}</b>
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>{bytes(html.length)}</span>
          {mobile && (
            <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
              <button type="button" style={{ ...tab(pane === 'preview'), flex: 'none', padding: '4px 10px' }} onClick={() => setPane('preview')}>Preview</button>
              <button type="button" style={{ ...tab(pane === 'code'), flex: 'none', padding: '4px 10px' }} onClick={() => setPane('code')}>{'<> Code'}</button>
            </div>
          )}
          <button type="button" onClick={onClose} style={{ marginLeft: mobile ? 0 : 'auto', background: 'none', border: 'none', color: 'var(--muted)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', minHeight: 40 }}>Close ✕</button>
        </div>
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: 12, padding: 12, overflow: 'auto' }}>
          {showPreview && (
            <div style={{ flex: '1 1 600px', minWidth: 0, minHeight: mobile ? 320 : 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>As it will arrive</div>
                <div style={{ display: 'flex', gap: 4, marginLeft: 'auto' }}>
                  <button type="button" style={{ ...tab(asPhone), flex: 'none', padding: '3px 10px' }} onClick={() => setAsPhone(true)} aria-pressed={asPhone}>Phone</button>
                  <button type="button" style={{ ...tab(!asPhone), flex: 'none', padding: '3px 10px' }} onClick={() => setAsPhone(false)} aria-pressed={!asPhone}>Computer</button>
                </div>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}><Frame html={rendered} height="100%" phone={asPhone} /></div>
            </div>
          )}
          {showCode && (
            <div style={{ flex: '1 1 420px', minWidth: 0, minHeight: mobile ? 260 : 0, display: 'flex', flexDirection: 'column' }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 5 }}>The HTML — edit it here</div>
              <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>{code(mobile ? 14 : 30)}</div>
            </div>
          )}
        </div>
        <div style={{ padding: '8px 14px', borderTop: '1px solid var(--hairline)', fontSize: 11.5, color: 'var(--muted)' }}>
          Rendered the same way the send does — tracked links, unsubscribe footer and merge fields filled from a sample recipient. Scripts never run here.
        </div>
      </div>
    </div>
  );
}
